import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionsAPI, billsAPI, groceryAPI, todoAPI, debtsAPI } from '../services/api';

function Dashboard({ user }) {
  const [stats, setStats] = useState({
    subscriptions: { count: 0, monthlyTotal: 0 },
    bills: { count: 0, unpaidTotal: 0, unpaidCount: 0 },
    grocery: { count: 0, uncheckedCount: 0 },
    todo: { count: 0, activeCount: 0 }
  });
  const [debtStats, setDebtStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    try {
      const [subsRes, billsRes, groceryRes, todoRes, debtsRes] = await Promise.all([
        subscriptionsAPI.getAll(),
        billsAPI.getAll(),
        groceryAPI.getAll(),
        todoAPI.getAll(),
        debtsAPI.getAll(),
      ]);

      const monthlyTotal = subsRes.data.reduce((total, sub) => {
        if (!sub.is_active) return total;
        let monthlyAmount = parseFloat(sub.amount);
        switch (sub.billing_cycle) {
          case 'weekly': monthlyAmount *= 4.33; break;
          case 'quarterly': monthlyAmount /= 3; break;
          case 'yearly': monthlyAmount /= 12; break;
          default: break;
        }
        return total + monthlyAmount;
      }, 0);

      const unpaidTotal = billsRes.data
        .filter(bill => !bill.is_paid)
        .reduce((total, bill) => total + parseFloat(bill.amount), 0);

      setStats({
        subscriptions: { count: subsRes.data.length, monthlyTotal },
        bills: {
          count: billsRes.data.length,
          unpaidTotal,
          unpaidCount: billsRes.data.filter(b => !b.is_paid).length
        },
        grocery: {
          count: groceryRes.data.length,
          uncheckedCount: groceryRes.data.filter(item => !item.is_checked).length
        },
        todo: {
          count: todoRes.data.length,
          activeCount: todoRes.data.filter(item => !item.is_completed).length
        }
      });

      // Compute debt stats
      const debts = debtsRes.data;
      if (debts.length > 0) {
        const totalBalance  = debts.reduce((s, d) => s + parseFloat(d.balance), 0);
        const totalOriginal = debts.reduce((s, d) => s + parseFloat(d.original_amount || d.balance), 0);
        const paidOff       = Math.max(0, totalOriginal - totalBalance);
        const payoffPct     = totalOriginal > 0 ? (paidOff / totalOriginal) * 100 : 0;
        const monthlyMin    = debts.reduce((s, d) => s + parseFloat(d.minimum_payment || 0), 0);

        const byPersonMap = {};
        debts.forEach(d => {
          const key = d.owner_id;
          if (!byPersonMap[key]) byPersonMap[key] = {
            name: d.owner_name || d.owner_username || 'Unknown',
            balance: 0,
            original: 0,
          };
          byPersonMap[key].balance  += parseFloat(d.balance);
          byPersonMap[key].original += parseFloat(d.original_amount || d.balance);
        });
        const byPerson = Object.values(byPersonMap).sort((a, b) => b.balance - a.balance);

        setDebtStats({ totalBalance, totalOriginal, paidOff, payoffPct, monthlyMin, byPerson, count: debts.length });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const fmt = (n) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>{getGreeting()}{user?.full_name ? `, ${user.full_name}` : ''}</h2>
          <p>Here's your Family Nest snapshot</p>
        </div>
      </div>

      {/* Household Stats */}
      <div className="stats-grid">
        <Link to="/subscriptions" className="stat-card-link" data-accent="coral">
          <div className="card">
            <div className="stat-icon coral">💳</div>
            <div className="stat-label">Monthly Subscriptions</div>
            <div className="stat-value">{fmt(stats.subscriptions.monthlyTotal)}</div>
            <div className="stat-sub">{stats.subscriptions.count} active services</div>
          </div>
        </Link>

        <Link to="/bills" className="stat-card-link" data-accent="teal">
          <div className="card">
            <div className="stat-icon teal">📄</div>
            <div className="stat-label">Upcoming Bills</div>
            <div className="stat-value">{fmt(stats.bills.unpaidTotal)}</div>
            <div className="stat-sub">{stats.bills.unpaidCount} unpaid</div>
          </div>
        </Link>

        <Link to="/grocery" className="stat-card-link" data-accent="amber">
          <div className="card">
            <div className="stat-icon amber">🛒</div>
            <div className="stat-label">Grocery Items</div>
            <div className="stat-value">{stats.grocery.uncheckedCount}</div>
            <div className="stat-sub">of {stats.grocery.count} items to buy</div>
          </div>
        </Link>

        <Link to="/todo" className="stat-card-link" data-accent="violet">
          <div className="card">
            <div className="stat-icon violet">✓</div>
            <div className="stat-label">Pending Tasks</div>
            <div className="stat-value">{stats.todo.activeCount}</div>
            <div className="stat-sub">of {stats.todo.count} total tasks</div>
          </div>
        </Link>
      </div>

      {/* Debt Overview */}
      {debtStats && (
        <>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.1em', color: 'var(--text-muted)',
            margin: '28px 0 12px',
          }}>
            Debt Overview
          </div>

          {/* Debt stat cards */}
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 16 }}>
            <Link to="/debts" className="stat-card-link">
              <div className="card">
                <div className="stat-icon" style={{ color: 'var(--danger)', fontSize: 22 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                  </svg>
                </div>
                <div className="stat-label">Total Debt</div>
                <div className="stat-value" style={{ color: 'var(--danger)' }}>{fmt(debtStats.totalBalance)}</div>
                <div className="stat-sub">{debtStats.count} debt{debtStats.count !== 1 ? 's' : ''} tracked</div>
              </div>
            </Link>

            <Link to="/debts" className="stat-card-link">
              <div className="card">
                <div className="stat-icon" style={{ color: 'var(--accent-teal)', fontSize: 22 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                    <polyline points="17 6 23 6 23 12"/>
                  </svg>
                </div>
                <div className="stat-label">Paid Off</div>
                <div className="stat-value" style={{ color: 'var(--accent-teal)' }}>{fmt(debtStats.paidOff)}</div>
                <div className="stat-sub">{debtStats.payoffPct.toFixed(1)}% of original balance</div>
              </div>
            </Link>

            <Link to="/debts" className="stat-card-link">
              <div className="card">
                <div className="stat-icon" style={{ color: 'var(--accent-amber)', fontSize: 22 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="5" width="20" height="14" rx="2"/>
                    <line x1="2" y1="10" x2="22" y2="10"/>
                  </svg>
                </div>
                <div className="stat-label">Min. Payments</div>
                <div className="stat-value" style={{ color: 'var(--accent-amber)' }}>{fmt(debtStats.monthlyMin)}</div>
                <div className="stat-sub">per month</div>
              </div>
            </Link>
          </div>

          {/* Payoff progress + per-person breakdown */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <h3 className="card-title" style={{ fontSize: 15 }}>Debt Payoff Progress</h3>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {fmt(debtStats.paidOff)} paid · {fmt(debtStats.totalBalance)} remaining
              </span>
            </div>
            <div className="card-body">

              {/* Overall progress bar */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Overall</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-teal)', fontFamily: "'JetBrains Mono', monospace" }}>
                    {debtStats.payoffPct.toFixed(1)}%
                  </span>
                </div>
                <div style={{ height: 12, borderRadius: 6, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 6,
                    width: `${Math.min(debtStats.payoffPct, 100)}%`,
                    background: 'linear-gradient(90deg, var(--accent-teal), var(--accent-blue))',
                    transition: 'width 0.8s ease',
                  }} />
                </div>
              </div>

              {/* Per-person rows */}
              {debtStats.byPerson.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {debtStats.byPerson.map((person, i) => {
                    const personPct = person.original > 0
                      ? Math.min(((person.original - person.balance) / person.original) * 100, 100)
                      : 0;
                    const barColors = [
                      'var(--accent-coral)', 'var(--accent-violet)',
                      'var(--accent-amber)', 'var(--accent-blue)',
                    ];
                    const color = barColors[i % barColors.length];
                    return (
                      <div key={person.name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{person.name}</span>
                          </div>
                          <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{personPct.toFixed(1)}% paid</span>
                            <span style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color }}>
                              {fmt(person.balance)}
                            </span>
                          </div>
                        </div>
                        <div style={{ height: 7, borderRadius: 4, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 4,
                            width: `${personPct}%`,
                            background: color,
                            transition: 'width 0.8s ease',
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Quick Actions */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: '16px' }}>Quick Actions</h3>
        </div>
        <div className="card-body">
          <div className="quick-actions-grid">
            <Link to="/subscriptions" className="btn btn-primary">+ Subscription</Link>
            <Link to="/bills" className="btn btn-primary">+ Bill</Link>
            <Link to="/grocery" className="btn btn-primary">+ Grocery Item</Link>
            <Link to="/todo" className="btn btn-primary">+ Task</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
